import { useEffect, useState, type ReactNode } from 'react';
import clsx from 'clsx';

import { useMediaQuery } from '../../useMediaQuery';
import { PanelResizeHandle } from '../ui/PanelResizeHandle';
import { FlowEmptyState } from './FlowEmptyState';
import { setFlowPanelsActionsNode } from './flow-panels-slots';
import {
  DIVIDER_PX,
  OVERLAY_QUERY,
  deriveFlowPanelsLayout,
  pxToShare,
  sideShareBounds,
  splitShareBounds,
  type FlowDividerId,
  type FlowPanelId,
} from './flow-panels-layout';
import {
  resetCollapsedChatShare,
  resetPanelShare,
  setCollapsedChatShare,
  setPanelShare,
  setPanelShares,
  useFlowPanelsStore,
} from './flow-panels-store';

export type FlowPanelsProps = {
  /** The Chat panel's content. */
  chat: ReactNode;
  /** The Actions panel's content. */
  actions: ReactNode;
  /** The Flow column's header — see the slot contract below. */
  header: ReactNode;
  /** The Flow column's content. */
  children: ReactNode;
};

/**
 * V2 Flow shell's three-column container: Chat · Flow · Actions (plan
 * `2026-09-30-005-v2-panel-container-plan.md`, unit U4). Renders whatever
 * `deriveFlowPanelsLayout` (`flow-panels-layout.ts`) derives from the store
 * (`flow-panels-store.ts`) plus a measured row width, and wires both inner
 * edges to `ui/PanelResizeHandle`.
 *
 * ── Slot contract (R13) — this comment IS the contract ───────────────────
 * `chat` and `actions` are plain `ReactNode` slots with ZERO compile-time
 * dependency on the components that will fill them (built in the sibling
 * `terra-design-v2-chat` / `terra-design-v2-queue-actions` worktrees, which
 * target `origin/master` directly and must stay independently mergeable).
 * Nothing in this file may import from either of them.
 *
 *   - `chat` / `actions` render ONLY while their panel is open and are
 *     UNMOUNTED when it closes. A slot owner that needs to survive a close
 *     (an in-flight chat session, a draft) persists it in its own store —
 *     this container deliberately makes no promise about slot lifetime.
 *   - `header` is always rendered as the Flow column's FIRST child — before
 *     `children` — and is hidden with the column (never unmounted) when
 *     collapsed (R5) or replaced by the empty state (R7), same D8 rule as
 *     `children`: a slot owner with live draft state (e.g. `GlobalSearch`'s
 *     typed query) survives a hide/show cycle because it's never torn down.
 *     `FlowPanels` wraps it in `div[data-flow-header]` and measures that
 *     wrapper's height for overlay placement (R10).
 *   - `children` is the Flow column and is ALWAYS mounted. When the column
 *     is collapsed (R5) or the empty state replaces it (R7) it is hidden
 *     with Tailwind `hidden` (`display: none`), never unmounted, so ledger
 *     scroll position and row selection survive (D8).
 *   - Additive: `useFlowPanelsActionsNode()` (`flow-panels-slots.ts`) exposes
 *     the Actions panel's live DOM node for a consumer that must stay
 *     mounted globally and can't be passed through the `actions` prop (which
 *     unmounts with the panel). It is a module-scope zustand store, NOT a
 *     context, because that consumer (`rail/RailDock.tsx`, mounted in
 *     `AppRouter.tsx` beside the Outlet) is not a descendant of this
 *     component. The `actions` prop stays the simple path for everyone else.
 *
 * ── Shares in, pixels only at the handle (D1) ────────────────────────────
 * The store holds fractions. This component measures the row with a
 * `ResizeObserver` and converts at the boundary against `layout.columnsWidth`
 * (the row width MINUS the divider gutters — dividers have `flex-grow: 0`,
 * so the three flex columns only ever share `rowWidth - DIVIDER_PX *
 * dividers.length`, never the raw `rowWidth`): `share * columnsWidth` into a
 * handle, `pxToShare(px, columnsWidth)` back out. Floors (which ARE pixels)
 * therefore become per-render bounds instead of stored state, and a window
 * resize re-proportions for free. Handles are driven by `layout.grows`, the
 * EFFECTIVE (reconciled) shares — never the raw store values, which are
 * requests that `deriveFlowPanelsLayout` clamps, not guarantees (see
 * `reconcileSideShares` in `flow-panels-layout.ts`). A both-open drag also
 * pins the other panel to its effective share (`commitSideShare`), so a
 * stale request never outlives one interaction. A degenerate row
 * (`*_FLOORS_PX`) freezes every handle and commits nothing.
 *
 * ── Modes ────────────────────────────────────────────────────────────────
 * `push` (default) lays the three columns out as flex siblings with
 * `flex-grow` = share and `flex-basis: 0`. Below `OVERLAY_QUERY` the mode
 * flips to `overlay` (D6): an open side panel floats over a full-width Flow
 * column at a proportional width, with no resize handles and no scrim —
 * a handle re-proportions neighbours, and a floating panel has none.
 *
 * Until the row has been measured (`rowWidth === 0` — first paint, or jsdom)
 * the columns render but the dividers do not (D5): their pixel bounds would
 * all be zero.
 */
export function FlowPanels({ chat, actions, header, children }: FlowPanelsProps) {
  const chatOpen = useFlowPanelsStore((s) => s.chatOpen);
  const actionsOpen = useFlowPanelsStore((s) => s.actionsOpen);
  const flowClosed = useFlowPanelsStore((s) => s.flowClosed);
  const chatShare = useFlowPanelsStore((s) => s.chatShare);
  const actionsShare = useFlowPanelsStore((s) => s.actionsShare);
  const collapsedChatShare = useFlowPanelsStore((s) => s.collapsedChatShare);

  const overlay = useMediaQuery(OVERLAY_QUERY);
  const mode = overlay ? 'overlay' : 'push';

  // Row measurement, copied from `account/AccountLens.tsx:152-168`: a
  // callback ref into `useState` so the observer (re)attaches whenever the
  // row itself mounts, and an observer created inside the effect and
  // disconnected in cleanup. No lifetime ref, so this is StrictMode-safe by
  // construction (setup -> cleanup -> setup can neither leak an observer nor
  // leave a ref stuck after the first mount).
  const [rowEl, setRowEl] = useState<HTMLDivElement | null>(null);
  const [rowWidth, setRowWidth] = useState(0);
  // The Flow header's wrapper — measured for R10 (below). `offsetHeight`, not
  // `clientWidth`: the header wraps to two rows below 48rem (R11), and the
  // overlay panels must start under whatever it actually paints.
  const [headerEl, setHeaderEl] = useState<HTMLDivElement | null>(null);
  const [headerHeight, setHeaderHeight] = useState(0);

  useEffect(() => {
    if (!rowEl) return;
    const measure = () => {
      setRowWidth(rowEl.clientWidth);
      setHeaderHeight(headerEl?.offsetHeight ?? 0);
    };
    measure();
    // ONE observer for both targets — `FlowPanels.test.tsx` stubs
    // `ResizeObserver` and keeps only the LAST constructed callback, so a
    // second observer would silently break every test's measurement.
    const ro = new ResizeObserver(measure);
    ro.observe(rowEl);
    if (headerEl) ro.observe(headerEl);
    return () => ro.disconnect();
  }, [rowEl, headerEl]);

  const layout = deriveFlowPanelsLayout({
    chatOpen,
    actionsOpen,
    flowClosed,
    chatShare,
    actionsShare,
    collapsedChatShare,
    mode,
    rowWidth,
  });

  const { columnsWidth, grows } = layout;
  // D5 — no measurement yet means no meaningful pixel bounds, so no handles.
  const showDividers = columnsWidth > 0;
  // A degenerate row (see `*_FLOORS_PX`) freezes each handle at its
  // effective width — `min === max === value` keeps the ARIA range valid —
  // and `commit*` below writes nothing, so the fallback never overwrites
  // the rep's stored request. Otherwise bounds use the EFFECTIVE
  // (reconciled) shares, never the raw store values — a stale raw share
  // would collapse the other handle's range.
  const frozen = (share: number) => ({ minShare: share, maxShare: share });
  const chatBounds = layout.degenerate
    ? frozen(grows.chat)
    : sideShareBounds({ rowWidth: columnsWidth, otherOpen: actionsOpen, otherShare: grows.actions });
  const actionsBounds = layout.degenerate
    ? frozen(grows.actions)
    : sideShareBounds({ rowWidth: columnsWidth, otherOpen: chatOpen, otherShare: grows.chat });
  const splitBounds = layout.degenerate ? frozen(grows.chat) : splitShareBounds({ rowWidth: columnsWidth });

  // Both open: the OTHER panel is pinned to its effective share in the same
  // write, so a stale request on the untouched panel can't re-absorb the
  // width the rep just freed (see `setPanelShares`). One open: the closed
  // panel's stored share is its memory for reopening — left alone.
  const commitSideShare = (id: FlowPanelId, px: number) => {
    if (layout.degenerate) return;
    const share = pxToShare(px, columnsWidth);
    if (chatOpen && actionsOpen) {
      setPanelShares(id === 'chat' ? { chat: share, actions: grows.actions } : { chat: grows.chat, actions: share });
    } else {
      setPanelShare(id, share);
    }
  };
  const commitSplitShare = (px: number) => {
    if (layout.degenerate) return;
    setCollapsedChatShare(pxToShare(px, columnsWidth));
  };

  // R10: overlay panels float UNDER the Flow column's header band, never over
  // it. `top-12` is the unmeasured fallback (first paint, jsdom). Once the
  // header wrapper is measured the inline `top` tracks its REAL rendered
  // height: the header wraps to two rows below 48rem (R11), and a hardcoded
  // 48px parked the floating Actions panel over the Header's own toggles —
  // the docked panel's only close control after R12 (Codex round 2).
  const overlayTop = headerHeight > 0 ? { top: headerHeight } : undefined;
  const overlayLeftProps = {
    'data-overlay': true,
    className:
      'absolute top-12 bottom-0 left-0 z-10 flex flex-col overflow-hidden border-r border-border-primary bg-bg-primary shadow-lg',
    style: { width: `${grows.chat * 100}%`, ...overlayTop },
  } as const;
  const overlayRightProps = {
    'data-overlay': true,
    className:
      'absolute top-12 bottom-0 right-0 z-10 flex flex-col overflow-hidden border-l border-border-primary bg-bg-primary shadow-lg',
    style: { width: `${grows.actions * 100}%`, ...overlayTop },
  } as const;
  const pushChatProps = {
    className: 'flex min-h-0 min-w-0 flex-col overflow-hidden',
    style: { flexGrow: grows.chat, flexBasis: 0 },
  } as const;
  const pushActionsProps = {
    className: 'flex min-h-0 min-w-0 flex-col overflow-hidden',
    style: { flexGrow: grows.actions, flexBasis: 0 },
  } as const;

  // Flow is always children[2] in both modes; reordering these slots
  // remounts `<Routes>` — see the header comment's "Why Flow remounts"
  // rationale (plan `2026-09-30-006-v2-panel-container-codex-fix-plan.md`).
  return (
    <div
      ref={setRowEl}
      data-flow-panels
      data-mode={mode}
      className="relative flex min-h-0 min-w-0 flex-1 flex-row overflow-hidden"
    >
      {chatOpen && (
        <div data-panel="chat" {...(overlay ? overlayLeftProps : pushChatProps)}>
          {chat}
        </div>
      )}

      {showDividers && layout.dividers.includes('chat-flow') && (
        <DividerFrame id="chat-flow">
          <PanelResizeHandle
            orientation="vertical"
            value={grows.chat * columnsWidth}
            min={chatBounds.minShare * columnsWidth}
            max={chatBounds.maxShare * columnsWidth}
            onValueChange={(px) => commitSideShare('chat', px)}
            onReset={() => resetPanelShare('chat')}
            label="Resize the chat panel"
          />
        </DividerFrame>
      )}

      <div
        data-panel="flow"
        className={clsx(
          'flex min-h-0 min-w-0 flex-col overflow-hidden',
          layout.collapsed && 'hidden',
        )}
        style={{ flexGrow: grows.flow, flexBasis: 0 }}
      >
        {/* Wrapper, not the slot element itself, so the measurement ref (R10)
            belongs to this file rather than to whatever the slot renders.
            `shrink-0` so the column's flex never squeezes the band. The
            Header's own `@container` root is this wrapper's only child, so
            the container width is still the column width. */}
        <div ref={setHeaderEl} data-flow-header className="shrink-0">
          {header}
        </div>
        {layout.showEmptyState && <FlowEmptyState />}
        <div
          data-flow-children
          className={clsx(
            'flex min-h-0 flex-1 flex-col overflow-hidden',
            layout.showEmptyState && 'hidden',
          )}
        >
          {children}
        </div>
      </div>

      {showDividers && layout.dividers.includes('chat-actions') && (
        <DividerFrame id="chat-actions">
          <PanelResizeHandle
            orientation="vertical"
            value={grows.chat * columnsWidth}
            min={splitBounds.minShare * columnsWidth}
            max={splitBounds.maxShare * columnsWidth}
            onValueChange={commitSplitShare}
            onReset={resetCollapsedChatShare}
            label="Resize the chat and actions split"
          />
        </DividerFrame>
      )}

      {showDividers && layout.dividers.includes('flow-actions') && (
        <DividerFrame id="flow-actions">
          <PanelResizeHandle
            orientation="vertical"
            // The Actions column sits AFTER its handle, so the drag/arrow
            // mapping inverts (D7's `side`).
            side="after"
            value={grows.actions * columnsWidth}
            min={actionsBounds.minShare * columnsWidth}
            max={actionsBounds.maxShare * columnsWidth}
            onValueChange={(px) => commitSideShare('actions', px)}
            onReset={() => resetPanelShare('actions')}
            label="Resize the actions panel"
          />
        </DividerFrame>
      )}

      {actionsOpen && (
        <div
          ref={setFlowPanelsActionsNode}
          data-panel="actions"
          {...(overlay ? overlayRightProps : pushActionsProps)}
        >
          {actions}
        </div>
      )}
    </div>
  );
}

/**
 * The `DIVIDER_PX`-wide corridor a vertical divider lives in (D12) — same
 * shape as `AccountLens.tsx:311-330`'s spacer, at a smaller width.
 * `flex-grow: 0` (via `shrink-0` + no grow, plus an explicit `width` instead
 * of a Tailwind size class) so the three columns' grows still sum to 1 over
 * `columnsWidth` — this constant IS the single source of truth shared with
 * `flow-panels-layout.ts`'s `columnsWidth` math; changing one without the
 * other reintroduces the gutter-math drift this component fixed. The static
 * 1px centre line is the resting state; the handle's own 2px line colours it
 * on hover/focus/drag.
 */
function DividerFrame({ id, children }: { id: FlowDividerId; children: ReactNode }) {
  return (
    <div data-divider={id} className="relative shrink-0" style={{ width: DIVIDER_PX }}>
      <div
        aria-hidden
        className="pointer-events-none absolute inset-y-0 left-1/2 w-px -translate-x-1/2 bg-border-primary"
      />
      {children}
    </div>
  );
}
